import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Separator } from "types/separator/separator.type";
import { CheckCircleIcon } from "@heroicons/react/20/solid";
import { FilePreview } from "components/FilePreview";
import { FileUploadButton } from "components/buttons/FileUploadButton";
import { useUploadFiles } from "api/image.api";
import {
  getSeparatorLabel,
  getSeparatorTypeLabel,
} from "types/separator/separator.helpers";

import { hasAvtal } from "types/separator/separator.type";
import { getMonthsString } from "helpers/date";
import { getFileName } from "helpers/file";
import {
  ContentContainer,
  HeaderContainer,
  ShadowCard,
} from "components/ShadowCard";
import { Headline4 } from "components/typography/Headline4";
import { DotMenu } from "components/DotMenu";
import { useCreateOrUpdateSeparatorLogic } from "../../../hooks/useCreateOrUpdateSeparatorLogic.hook";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { useDeleteSeparator, useSetSeparatorFiles } from "api/separator.api";
import Modal from "components/Modal";
import { FormProvider } from "react-hook-form";
import { SeparatorMetadataForm } from "./SeparatorMetadataForm";
import DangerModal from "components/DangerModal";
import {
  DetailsList,
  Item,
  Label,
  Value,
} from "components/detailsList/DetailsList";
import { useIsAdmin } from "store/user.store";
import { useQuery } from "@tanstack/react-query";
import { getScheduledMaintenanceItem } from "api/scheduledMaintenance.api";
import { getNextInspectionLabel } from "types/separator/initialInspectionDate.helpers";

interface Props {
  separator: Separator;
}
export const SeparatorDetailsTab: FC<Props> = ({
  separator: separatorWithWrongFiles,
}) => {
  const isAdmin = useIsAdmin();
  const [tempDeletedFiles, setTempDeletedFiles] = useState<number[]>([]);

  const separator = useMemo(
    () => ({
      ...separatorWithWrongFiles,
      files: separatorWithWrongFiles.files?.filter(
        (file) => !tempDeletedFiles.includes(file.id),
      ),
    }),
    [separatorWithWrongFiles, tempDeletedFiles],
  );
  // separator = {
  //   ...separator,
  // files: separator.files?.filter(
  //   (file) => !tempDeletedFiles.includes(file.id),
  // ),
  // };
  // Patch Separator
  const patchSeparatorLogic = useCreateOrUpdateSeparatorLogic(
    undefined,
    undefined,
    separator,
  );

  // Nästa tillfälle per besiktningstyp: bokat uppdrag, beräknat från
  // historik eller första planerade datum.
  const { data: scheduleItem } = useQuery({
    ...getScheduledMaintenanceItem(separator.facility?.id ?? -1),
    enabled: !!separator.facility?.id,
  });
  const scheduleDto = scheduleItem?.separatorDtoList.find(
    (x) => x.separator.id === separator.id,
  );
  const nextFiveYearsLabel = getNextInspectionLabel(
    scheduleDto?.fiveYearsInspectionInfo,
  );
  const nextSixMonthsLabel = getNextInspectionLabel(
    scheduleDto?.sixMonthsInspectionInfo,
  );

  // Delete separator
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [isDeleteSeparatorModalOpen, setIsDeleteSeparatorModalOpen] =
    useState<boolean>(false);

  const {
    mutate: deleteSeparator,
    // isPending: isDeletingSeparator,
    isSuccess: isDeleteSeparatorSuccess,
  } = useDeleteSeparator(separator.id);

  const handleDeleteSeparator = () => deleteSeparator(); // för att slippa skriva komplexa props

  useEffect(() => {
    if (isDeleteSeparatorSuccess) {
      // Routa till kund/avskiljarelista
      if (!separator) {
        console.error("Hittar inget objekt");
        return;
      }
      navigate({
        to: "/app/kunder/$customerId/facility/$facilityId",
        params: {
          customerId: separator.facility.customer.id.toString(),
          facilityId: separator.facility.id.toString(),
        },
      });
    }
  }, [isDeleteSeparatorSuccess, navigate, pathname, separator]);

  // Filer, wtf

  const { mutate: setFiles, isPending: isSettingFiles } =
    useSetSeparatorFiles();

  const afterUploadedFile = useCallback(
    (url: string) => {
      if (separator) {
        setFiles({
          separatorId: separator.id,
          files: [...separator.files, { id: undefined, name: url, url }],
        });
      }
    },
    [separator, setFiles],
  );

  const {
    mutate: uploadImages,
    isSuccess: isUploadImagesSuccess,
    data: uploadedImages,
    isPending: isUploadingImages,
    reset,
  } = useUploadFiles();

  const onFileAdd = async (blob: Blob | null, filename: string) => {
    if (!blob) return;
    const file = new File([blob], filename);
    if (!file) {
      return;
    }

    uploadImages([file]);
  };

  const deleteFile = (id: number) => {
    setTempDeletedFiles((prev) => [...prev, id]);
    if (separator) {
      setFiles({
        separatorId: separator.id,
        files: (separator.files || []).filter((file) => file.id !== id),
      });
    }
  };

  useEffect(() => {
    if (isUploadImagesSuccess && uploadedImages) {
      // Här ska jag uppdatera inspection med de nya bilderna
      afterUploadedFile(uploadedImages[0]);

      reset();
    }
  }, [isUploadImagesSuccess, uploadedImages, reset, afterUploadedFile]);

  return (
    <ShadowCard className="max-w-5xl">
      <HeaderContainer>
        <Headline4>Detaljer</Headline4>
        <DotMenu
          items={[
            {
              label: "Redigera",
              isEdit: true,
              onClick: patchSeparatorLogic.openModal,
            },
            {
              label: "Ta bort objekt",
              onClick: () => setIsDeleteSeparatorModalOpen(true),
              isDelete: true,
            },
          ].filter((x) => x.label !== "Ta bort objekt" || isAdmin)}
        />

        <Modal
          isOpen={patchSeparatorLogic.isModalOpen}
          title={"Ändra objekt"}
          onClose={patchSeparatorLogic.onModalClose}
          afterClose={patchSeparatorLogic.afterClose}
          actionHandler={patchSeparatorLogic.onSubmit}
          actionText="Spara"
          isLoading={patchSeparatorLogic.isPending}
          loadingText="Sparar..."
        >
          <FormProvider {...patchSeparatorLogic.form}>
            <SeparatorMetadataForm
              inspectionSchedule={patchSeparatorLogic.inspectionSchedule}
            />
          </FormProvider>
        </Modal>

        <DangerModal
          isOpen={isDeleteSeparatorModalOpen}
          onClose={() => setIsDeleteSeparatorModalOpen(false)}
          actionText="Ta bort objektet"
          actionHandler={handleDeleteSeparator}
          title="Ta bort objektet"
        />
      </HeaderContainer>
      <ContentContainer>
        <DetailsList>
          <Item>
            <Label>{getSeparatorLabel("type")}</Label>
            <Value>{getSeparatorTypeLabel(separator)}</Value>
          </Item>
          <Item>
            <Label>{getSeparatorLabel("identifier")}</Label>
            <Value>{separator.identifier}</Value>
          </Item>
          {/* <Item>
          <Label>{getSeparatorLabel("description")}</Label>
          <Value>{separator.description}</Value>
        </Item> */}

          <Item>
            <Label>Påminnelser</Label>
            <Value>
              {hasAvtal(separator) ? (
                <ul className="space-y-1">
                  {separator.avtalFemarskontroll && (
                    <CheckedAvtal
                      label={getSeparatorLabel("avtalFemarskontroll")}
                      detail={nextFiveYearsLabel}
                    />
                  )}
                  {separator.avtalHalvarsKontroll && (
                    <CheckedAvtal
                      label={getSeparatorLabel("avtalHalvarsKontroll")}
                      detail={nextSixMonthsLabel}
                    />
                  )}
                  {separator.avtalSlamtomning && (
                    <CheckedAvtal
                      label={`Tömning (${getMonthsString(
                        separator.tomningReminder,
                      )})`.replace("()", "")}
                    />
                  )}
                  {separator.avtalVattenprovtagning && (
                    <CheckedAvtal
                      label={`Vattenprovtagning (${getMonthsString(
                        separator.vattenprovtagningMonths,
                      )})`.replace("()", "")}
                    />
                  )}
                </ul>
              ) : (
                ""
              )}
            </Value>
          </Item>

          <Item>
            <Label>{getSeparatorLabel("coordinates")}</Label>
            <Value>
              {!!separator.coordinates && (
                <a
                  href={`http://www.google.com/maps/place/${separator.coordinates.replace(
                    " ",
                    "",
                  )}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-cyan-600 underline"
                >
                  Öppna Google Maps
                </a>
              )}
            </Value>
          </Item>
          <Item>
            <Label>{getSeparatorLabel("propertyDesignation")}</Label>
            <Value>{separator.propertyDesignation}</Value>
          </Item>
          <Item>
            <Label>Filer</Label>
            <Value>
              <div className="space-y-4 pb-2">
                {separator.files?.map(({ id, url }) => (
                  <FilePreview
                    key={id}
                    url={url}
                    onDelete={() => deleteFile(id)}
                    name={getFileName(url)}
                  />
                ))}
                <FileUploadButton
                  onFileAdd={onFileAdd}
                  isLoading={isUploadingImages || isSettingFiles}
                />
              </div>
            </Value>
          </Item>
          <Item>
            <Label>{getSeparatorLabel("notes")}</Label>
            <Value>{separator.notes}</Value>
          </Item>
        </DetailsList>
      </ContentContainer>
    </ShadowCard>
  );
};

const CheckedAvtal: FC<{ label: string; detail?: string }> = ({
  label,
  detail,
}) => (
  <li>
    <span className={"text-900-700 mr-6 flex items-center text-sm"}>
      <CheckCircleIcon
        className="mr-1 h-4 w-4 flex-shrink-0 text-cyan-500"
        aria-hidden="true"
      />
      {label}
      {detail && (
        <span className="ml-2 text-gray-600">
          Nästa tillfälle: <span className="text-gray-900">{detail}</span>
        </span>
      )}
    </span>
  </li>
);
